// src/pages/seasons/AttendanceSession.jsx
'use client';

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, orderBy,
  onSnapshot, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  UserGroupIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import TeamSubNav from '@/components/team/TeamSubNav';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

const STATUS = { present: 'present', absent: 'absent', unset: 'unset' };
const FILTERS = ['all', 'present', 'absent', 'unmarked'];

const RSVP_META = {
  going:     { label: 'Going',     color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', emoji: '✅' },
  maybe:     { label: 'Maybe',     color: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200',     emoji: '🤔' },
  not_going: { label: 'Not Going', color: 'text-red-600',     bg: 'bg-red-50 border-red-200',         emoji: '❌' },
};

const APP_BASE = import.meta.env.VITE_APP_URL || 'https://app.getscrimly.com';

export default function AttendanceSession() {
  const { seasonId, sessionId } = useParams();
  const navigate = useNavigate();

  const [session,   setSession]   = useState(null);
  const [players,   setPlayers]   = useState([]);
  const [records,   setRecords]   = useState({});
  const [rsvps,     setRsvps]     = useState({});  // { registrationId: 'going'|'maybe'|'not_going' }
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [dirty,     setDirty]     = useState(false);
  const [filter,    setFilter]    = useState('all');

  // Multi-coach race protection.
  //
  // `dirtyRows` tracks which player rows the local coach has edited but
  // not yet saved. The records `onSnapshot` listener preserves these
  // rows from the previous local state — without this, a concurrent
  // save by another coach would arrive via Firestore, replace the
  // whole records map, and silently overwrite the local coach's
  // in-progress edits ("I tapped Marco as absent and it just flipped
  // back to present!"). Cleared after a successful save commits.
  const [dirtyRows, setDirtyRows] = useState(() => new Set());
  const dirtyRowsRef = useRef(dirtyRows);
  useEffect(() => { dirtyRowsRef.current = dirtyRows; }, [dirtyRows]);

  /* ── Load session ── */
  useEffect(() => {
    getDoc(doc(db, 'sessions', sessionId)).then(snap => {
      if (snap.exists()) setSession({ id: snap.id, ...snap.data() });
    });
  }, [sessionId]);

  /* ── Load confirmed registrations ── */
  // session.teamId is required by Firestore security rules
  useEffect(() => {
    if (!session?.teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('teamId',  '==', session.teamId),
      where('seasonId', '==', seasonId),
      where('status', 'in', ['confirmed', 'paid']),
      orderBy('playerName', 'asc')
    );
    return onSnapshot(q, snap => {
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [seasonId, session?.teamId]);

  /* ── Load attendance records ── */
  useEffect(() => {
    const q = query(collection(db, 'attendance'), where('sessionId', '==', sessionId));
    return onSnapshot(q, snap => {
      const serverMap = {};
      snap.docs.forEach(d => { const data = d.data(); serverMap[data.registrationId] = data.status; });
      // Preserve any rows the local coach has edited but not yet saved
      // — without this guard, a concurrent save by another coach would
      // wipe the local coach's mid-edit state. Read dirty rows via a
      // ref so the callback doesn't capture a stale Set.
      setRecords(prev => {
        const next = { ...serverMap };
        const dirty = dirtyRowsRef.current;
        for (const id of dirty) {
          if (id in prev) next[id] = prev[id];
        }
        return next;
      });
      setLoading(false);
    });
  }, [sessionId]);

  /* ── Load RSVPs ── */
  useEffect(() => {
    const q = query(collection(db, 'rsvps'), where('sessionId', '==', sessionId));
    return onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        map[data.registrationId] = { status: data.status, note: data.note };
      });
      setRsvps(map);
    });
  }, [sessionId]);

  /* ── Toggle attendance ── */
  const toggle = (playerId) => {
    setRecords(prev => {
      const current = prev[playerId] ?? STATUS.unset;
      const next =
        current === STATUS.unset   ? STATUS.present :
        current === STATUS.present ? STATUS.absent  :
        STATUS.unset;
      return { ...prev, [playerId]: next };
    });
    // Mark this row dirty so the records onSnapshot listener won't
    // overwrite our local edit if another coach saves concurrently,
    // and so save() knows to write only the rows we touched.
    setDirtyRows(s => {
      const next = new Set(s);
      next.add(playerId);
      return next;
    });
    setDirty(true);
  };

  const markAll = (status) => {
    const next = {};
    players.forEach(p => { next[p.id] = status; });
    setRecords(next);
    // Bulk mark touches every player on screen — flag them all dirty
    // so the listener preserves our overlay until we save.
    setDirtyRows(new Set(players.map(p => p.id)));
    setDirty(true);
  };

  /* ── Save attendance ── */
  //
  // Only writes the rows the local coach has actively edited (tracked
  // in `dirtyRows`). Pre-fix this iterated all `players` and wrote
  // every row regardless — which meant a coach saving with a stale
  // local view of player #3 would overwrite another coach's recent
  // edit on #3. Writing only dirty rows means concurrent edits on
  // different rows commute safely, and the records onSnapshot pulls
  // in everyone else's writes for the read side.
  const save = async () => {
    const dirty = dirtyRowsRef.current;
    if (dirty.size === 0) return; // nothing to do

    setSaving(true);
    try {
      const batch = writeBatch(db);
      const playerById = new Map(players.map(p => [p.id, p]));
      let writeCount = 0;

      for (const playerId of dirty) {
        const player = playerById.get(playerId);
        if (!player) continue; // player roster changed; skip orphan
        const status = records[playerId] ?? STATUS.unset;
        const ref = doc(collection(db, 'attendance'), `${sessionId}_${playerId}`);
        batch.set(ref, {
          sessionId,
          seasonId,
          // teamId stamped on every doc for future rule-tightening + cross-
          // team analytics. Firestore rules currently allow any
          // authenticated read/write on /attendance, but we want to lock
          // them down to team staff later — adding teamId now means we
          // don't have to backfill thousands of docs at that point.
          teamId:         session?.teamId || null,
          registrationId: playerId,
          playerName:     player.playerName,
          status,
          updatedAt:      serverTimestamp(),
        }, { merge: true });
        writeCount++;
      }

      // Session-level counters are derived/approximate. Two coaches
      // saving concurrently will both write their own count based on
      // their local view; last write wins. The records onSnapshot
      // delivers the authoritative state to both clients regardless.
      // For an exact count we'd move this to a Firestore trigger —
      // not worth it pre-launch.
      const present = players.filter(p => records[p.id] === STATUS.present).length;
      batch.update(doc(db, 'sessions', sessionId), {
        attendanceCount: players.length,
        presentCount:    present,
        updatedAt:       serverTimestamp(),
      });

      await batch.commit();
      setDirtyRows(new Set());
      setDirty(false);
      toast.success(
        writeCount === 1
          ? 'Saved 1 change.'
          : `Saved ${writeCount} changes.`
      );
    } catch (err) {
      console.error('[AttendanceSession]', err);
      toast.error('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Copy RSVP link ── */
  const copyRsvpLink = (registrationId) => {
    const url = `${APP_BASE}/rsvp/${sessionId}/${registrationId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('RSVP link copied!'));
  };

  /* ── Copy all RSVP links for WhatsApp ── */
  const copyAllLinks = () => {
    const sessionDate = toDate(session?.date);
    const dateStr = sessionDate ? format(sessionDate, 'MMM d') : 'upcoming session';
    const lines = players.map(p =>
      `${p.playerName}: ${APP_BASE}/rsvp/${sessionId}/${p.id}`
    );
    const text = `Please RSVP for ${session?.type || 'our session'} on ${dateStr}:\n\n${lines.join('\n')}`;
    navigator.clipboard.writeText(text).then(() => toast.success('All RSVP links copied for WhatsApp!'));
  };

  /* ── Stats ── */
  const stats = useMemo(() => {
    const present  = players.filter(p => records[p.id] === STATUS.present).length;
    const absent   = players.filter(p => records[p.id] === STATUS.absent).length;
    const unmarked = players.length - present - absent;
    const rsvpGoing    = Object.values(rsvps).filter(r => r.status === 'going').length;
    const rsvpMaybe    = Object.values(rsvps).filter(r => r.status === 'maybe').length;
    const rsvpNotGoing = Object.values(rsvps).filter(r => r.status === 'not_going').length;
    return { present, absent, unmarked, total: players.length, rsvpGoing, rsvpMaybe, rsvpNotGoing };
  }, [players, records, rsvps]);

  /* ── Filtered list ── */
  const filteredPlayers = useMemo(() => {
    if (filter === 'all') return players;
    return players.filter(p => {
      const status = records[p.id] ?? STATUS.unset;
      if (filter === 'present')  return status === STATUS.present;
      if (filter === 'absent')   return status === STATUS.absent;
      if (filter === 'unmarked') return status === STATUS.unset;
      return true;
    });
  }, [players, records, filter]);

  const sessionDate = toDate(session?.date);

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={session?.teamId} />

        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/seasons/${seasonId}/attendance`)}
            className="mb-3 flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back to Schedule
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}
              >
                Attendance
              </p>
              <h1
                className="uppercase text-[#0d1f14] leading-[1.05]"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 28 }}
              >
                {session?.type ?? 'Session'}
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                {sessionDate ? format(sessionDate, 'EEEE, MMMM d, yyyy') : ''}
                {session?.note ? ` · ${session.note}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {players.length > 0 && (
                <button
                  type="button"
                  onClick={copyAllLinks}
                  className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <LinkIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
                  Copy All RSVP Links
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate(`/seasons/${seasonId}/lineup/${sessionId}`)}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ClipboardDocumentListIcon className="h-4 w-4 text-[#5a8a1f]" />
                Lineup Builder
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {saving
                  ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />Saving…</>
                  : 'Save Attendance'
                }
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(n => <div key={n} className="h-16 rounded-xl bg-zinc-100 animate-pulse" />)}
          </div>
        ) : players.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-12 text-center">
            <UserGroupIcon className="mx-auto h-10 w-10 text-zinc-300" />
            <h3
              className="mt-4 text-[#0d1f14] uppercase"
              style={{ fontFamily: HEAD_FONT, fontWeight: 700, fontSize: 18 }}
            >
              No confirmed players
            </h3>
            <p className="mt-1 text-sm text-zinc-500">Players appear here once their registration is confirmed.</p>
          </div>
        ) : (
          <>
            {/* Stats bar */}
            <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Attendance summary */}
              <div className="flex items-center gap-4 rounded-2xl bg-white border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-3 text-sm">
                <span
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Attendance
                </span>
                <span className="flex items-center gap-1.5 text-emerald-700 font-semibold">
                  <CheckCircleIcon className="h-4 w-4" /> {stats.present}
                </span>
                <span className="flex items-center gap-1.5 text-red-500 font-semibold">
                  <XCircleIcon className="h-4 w-4" /> {stats.absent}
                </span>
                <span className="text-zinc-400">{stats.unmarked} unmarked</span>
              </div>

              {/* RSVP summary */}
              <div className="flex items-center gap-3 rounded-2xl bg-white border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-3 text-sm">
                <span
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  RSVPs
                </span>
                <span className="text-emerald-700 font-semibold">✅ {stats.rsvpGoing}</span>
                <span className="text-amber-700 font-semibold">🤔 {stats.rsvpMaybe}</span>
                <span className="text-red-500 font-semibold">❌ {stats.rsvpNotGoing}</span>
                {stats.rsvpGoing + stats.rsvpMaybe + stats.rsvpNotGoing === 0 && (
                  <span className="text-zinc-400 text-xs">No responses yet</span>
                )}
              </div>
            </div>

            {/* Filter chips + bulk-mark row */}
            <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 flex-wrap">
                {FILTERS.map(key => {
                  const count =
                    key === 'all'      ? stats.total :
                    key === 'present'  ? stats.present :
                    key === 'absent'   ? stats.absent :
                    stats.unmarked;
                  const isActive = filter === key;
                  let activeCls = 'bg-[#0d1f14] text-white border-[#0d1f14]';
                  let inactiveCls = 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5';
                  if (key === 'present') {
                    activeCls = 'bg-emerald-600 text-white border-emerald-600';
                    inactiveCls = 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100';
                  } else if (key === 'absent') {
                    activeCls = 'bg-red-600 text-white border-red-600';
                    inactiveCls = 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
                  }
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilter(key)}
                      className={clsx(
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs uppercase transition-colors',
                        isActive ? activeCls : inactiveCls,
                      )}
                      style={{ fontFamily: HEAD_FONT, fontWeight: 700, letterSpacing: '0.6px' }}
                    >
                      {key}
                      <span className={clsx(
                        'inline-flex items-center justify-center rounded-full px-1.5 text-[10px]',
                        isActive ? 'bg-white/20 text-white' : 'bg-white/60 text-current',
                      )}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Bulk mark */}
              <div className="flex items-center gap-1.5">
                <span
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-zinc-400 mr-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Mark all
                </span>
                <button
                  type="button"
                  onClick={() => markAll(STATUS.present)}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold uppercase text-emerald-700 hover:bg-emerald-100 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Present
                </button>
                <button
                  type="button"
                  onClick={() => markAll(STATUS.absent)}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold uppercase text-red-700 hover:bg-red-100 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Absent
                </button>
              </div>
            </div>

            {/* Player list */}
            <div className="space-y-2">
              {filteredPlayers.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500">
                  No players match this filter.
                </div>
              ) : filteredPlayers.map(player => {
                const attStatus = records[player.id] ?? STATUS.unset;
                const rsvp = rsvps[player.id];
                const rsvpMeta = rsvp ? RSVP_META[rsvp.status] : null;

                return (
                  <div
                    key={player.id}
                    className={clsx(
                      'flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all',
                      attStatus === STATUS.present && 'border-emerald-200 bg-emerald-50',
                      attStatus === STATUS.absent  && 'border-red-200 bg-red-50',
                      attStatus === STATUS.unset   && 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5',
                    )}
                  >
                    {/* Attendance toggle — 44px minimum tap target (WCAG
                        2.5.5). Coach is on a sideline in sun on a phone;
                        the previous 36px target produced misses. May 2026
                        sized up to h-11 w-11. Iconography stayed h-5 w-5
                        so the lime/red/emerald fills still feel intentional
                        rather than over-padded. */}
                    <button
                      type="button"
                      onClick={() => toggle(player.id)}
                      className={clsx(
                        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        attStatus === STATUS.present && 'border-emerald-500 bg-emerald-500',
                        attStatus === STATUS.absent  && 'border-red-500 bg-red-500',
                        attStatus === STATUS.unset   && 'border-zinc-300 bg-white hover:border-[#b5f03d]',
                      )}
                      title="Toggle attendance"
                    >
                      {attStatus === STATUS.present && <CheckCircleSolid className="h-5 w-5 text-white" />}
                      {attStatus === STATUS.absent  && <XCircleIcon className="h-5 w-5 text-white" />}
                    </button>

                    {/* Player info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-[#0d1f14]">
                          {player.playerName}
                        </p>
                        {/* RSVP badge */}
                        {rsvpMeta && (
                          <span className={clsx(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase',
                            rsvpMeta.bg, rsvpMeta.color,
                          )}
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                          >
                            {rsvpMeta.emoji} {rsvpMeta.label}
                          </span>
                        )}
                        {!rsvpMeta && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-zinc-200 px-2 py-0.5 text-[10px] uppercase text-zinc-400"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                          >
                            No RSVP
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-400 truncate">
                        {player.parentName}
                        {rsvp?.note ? ` · "${rsvp.note}"` : ''}
                      </p>
                    </div>

                    {/* Attendance label */}
                    <span
                      className={clsx(
                        'shrink-0 text-xs uppercase hidden sm:block',
                        attStatus === STATUS.present && 'text-emerald-700',
                        attStatus === STATUS.absent  && 'text-red-600',
                        attStatus === STATUS.unset   && 'text-zinc-400',
                      )}
                      style={{ fontFamily: HEAD_FONT, fontWeight: 700, letterSpacing: '0.6px' }}
                    >
                      {attStatus === STATUS.present ? 'Present' : attStatus === STATUS.absent ? 'Absent' : 'Tap'}
                    </span>

                    {/* Copy RSVP link */}
                    <button
                      type="button"
                      onClick={() => copyRsvpLink(player.id)}
                      title="Copy RSVP link"
                      className="shrink-0 p-1.5 rounded-lg text-zinc-300 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Floating save hint */}
            {dirty && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
                <div className="flex items-center gap-3 rounded-full bg-[#0d1f14] px-5 py-2.5 shadow-xl text-white text-sm">
                  <span>Unsaved changes</span>
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
